import { refreshToken } from '@/apollo'
import { AUTH_ERROR_CODES } from '@/constants'
import { tokenManager } from '@/services/TokenManager/TokenManager'
import axios, { CreateAxiosDefaults } from 'axios'

function isAuthError(error: {
  response?: {
    status?: number
    data?: { errors?: Array<{ extensions?: { code?: string } }> }
  }
}): boolean {
  const status = error.response?.status
  if (status === 401) return true
  if (status === 403) {
    return (
      error.response?.data?.errors?.some(e =>
        AUTH_ERROR_CODES.has(e.extensions?.code ?? '')
      ) ?? false
    )
  }
  return false
}

export function createAxiosInstance(config: CreateAxiosDefaults = {}) {
  const instance = axios.create(config)

  // Request interceptor: decode JWT exp and refresh before sending if
  // expired/about to expire. Prevents TOKEN_EXPIRED from ever reaching the server.
  instance.interceptors.request.use(async requestConfig => {
    const token = await tokenManager.getValidAccessToken()
    if (token) {
      requestConfig.headers.set('Authorization', `Bearer ${token}`)
    }
    return requestConfig
  })

  // Response interceptor: safety net for edge cases where the token
  // expired between the pre-request check and the server receiving it.
  instance.interceptors.response.use(
    response => response,
    async error => {
      const originalRequest = error.config
      if (
        isAuthError(error) &&
        // eslint-disable-next-line no-underscore-dangle
        !originalRequest._retry
      ) {
        // ENG-1633: if the manager is already torn down (logout / redirect in
        // progress, e.g. a late practice-area or waterfall request firing after
        // the user left /createNewCase), don't attempt a refresh — it would
        // throw "TokenManager destroyed". Reject with the original auth error so
        // the caller's existing handler surfaces a normal error state.
        if (tokenManager.isDestroyed()) {
          return Promise.reject(error)
        }
        // eslint-disable-next-line no-underscore-dangle
        originalRequest._retry = true
        try {
          await refreshToken()
          return await instance(originalRequest)
        } catch (refreshError) {
          // eslint-disable-next-line no-console
          console.error('Token refresh failed:', refreshError)
          return Promise.reject(refreshError)
        }
      }
      return Promise.reject(error)
    }
  )

  return instance
}
